import { ref, watch } from 'vue'
import store from '@/store'
import { config } from '@/config.js'
import { useToast } from '@/composables/common/useToast'

/**
 * Returns true when a seat can be reassigned to the current subscriber
 * in the admin cambiar-asiento flow.
 *
 * A seat is considered asignable when its `ocupado` flag is:
 *   0 = libre (free)
 *   3 = bloqueada (purple, admin reassignment is allowed)
 *
 * Real occupied seats (status 1/true and status 2/pendiente) are NOT
 * considered asignable here and remain blocked.
 *
 * @param {number|boolean} ocupado
 * @returns {boolean}
 */
function isAsientoAsignable(ocupado) {
    return ocupado === 0 || ocupado === 3
}

/**
 * useCambiarAsiento
 * Composable that manages seat selection state and asiento fetching logic
 * for the CambiarAsiento modal.
 *
 * @param {Ref} zonaRef - ref to the zona object
 * @param {Object} options - optional configuration
 * @param {Function} options.onAutoSelect - callback when zona.with_seats is false
 * @returns {Object} - public API with filas, loading, getAsientos, submitting, toggleSeleccion
 */
export function useCambiarAsiento(zonaRef, { onAutoSelect } = {}) {
    const toast = useToast()
    const filas = ref(null)
    const loading = ref(false)
    const submitting = ref(false)

    /**
     * Fetch and populate filas from API
     * Called when zona changes and zona.with_seats is true
     */
    async function getAsientos() {
        if (!zonaRef.value) {
            return
        }

        if (!zonaRef.value.with_seats) {
            // If zona doesn't have seats, call the callback and return
            if (onAutoSelect) {
                onAutoSelect()
            }
            return
        }

        // Fetch asientos configuration
        loading.value = true
        const params = {
            filters: {
                zona_id: zonaRef.value.id,
                conf: true
            }
        }

        try {
            const res = await store.dispatch(config.getVuexAlias('zonas.index'), params)
            filas.value = res.data.configuracion_asientos || null
        } catch (error) {
            console.error('Error fetching asientos:', error)
            if (toast && toast.error) {
                toast.error('Error al cargar los asientos')
            }
        } finally {
            loading.value = false
        }
    }

    /**
     * Toggle seat selection and dispatch the asiento change API call.
     * Handles loading state and toast notifications internally.
     *
     * Seats with `ocupado === 3` (bloqueada) are accepted as asignable so
     * admins can reassign a subscriber into a bloqueada seat while keeping
     * its purple visual state. Seats with other occupied statuses remain
     * blocked and surface the existing "ocupado" toast.
     *
     * @param {number} fila - Row number
     * @param {number} asiento - Seat number
     * @param {number} ocupado - Occupied flag (0 = free, 3 = bloqueada, others = blocked)
     * @param {number|null} plaza_id - Plaza ID for plaza-based selection
     * @param {Object} options
     * @param {number} options.abonado_id - Subscriber ID (required)
     * @param {Object} options.zona - Zone object with id and tiene_plazas
     * @param {Function} options.onSuccess - Callback after successful dispatch
     */
    async function toggleSeleccion(fila, asiento, ocupado, plaza_id = null, { abonado_id, zona, onSuccess } = {}) {
        if (submitting.value) {
            return
        }
        submitting.value = true

        const params = { abonado_id }

        if (plaza_id) {
            params.plaza_id = plaza_id
        } else {
            params.asiento = {
                row: fila,
                seat: asiento,
                region_id: zona.id
            }
        }

        if (isAsientoAsignable(ocupado)) {
            try {
                await store.dispatch(config.getVuexAlias('back.abonados.asiento'), params)
                toast.success(!zona.tiene_plazas ? '¡Asiento editado!' : '¡Plaza editada!')
                if (onSuccess) onSuccess()
            } catch (err) {
                const errorMessage = err.response?.data?.message || (!zona.tiene_plazas ? 'El asiento esta ocupado' : 'La plaza está ocupada')
                toast.error(errorMessage)
            } finally {
                submitting.value = false
            }
        } else {
            toast.error(!zona.tiene_plazas ? 'El asiento esta ocupado' : 'La plaza está ocupada')
            submitting.value = false
        }
    }

    // Watch zonaRef for changes
    watch(
        zonaRef,
        newVal => {
            if (newVal) {
                getAsientos()
            }
        },
        { immediate: true }
    )

    // ==================== PUBLIC API ====================
    return {
        filas,
        loading,
        submitting,
        getAsientos,
        toggleSeleccion
    }
}
